import { createBrowserRouter, type RouteObject } from 'react-router';
import { ActivitiesGuard } from './ActivitiesGuard';
import { EnglishPages } from './EnglishPages';
import { LegacyExperienceQuery } from './LegacyExperienceQuery';
import { redirectTo, redirectToAlias } from './legacyUrls';
import { RouteError } from './RouteError';
import { RootLayout } from './RootLayout';
import { paths } from './paths';
import type { ShellHandle } from './shell/shellConfig';
import { PrivateSeo } from './PrivateSeo';

const shell = (s: ShellHandle) => ({ shell: s });

/** Lazy page: each page is its own chunk. */
const page = (load: () => Promise<{ default: React.ComponentType }>) => async () => ({
  Component: (await load()).default,
});

/**
 * Lazy page rendered with extra elements beside it (home rating prompt, legacy query adapters, head tags of a
 * page this file does not own), without touching the page itself.
 */
const pageWith =
  (load: () => Promise<{ default: React.ComponentType }>, extras: () => Promise<React.ReactNode>) => async () => {
    const [{ default: Page }, extra] = await Promise.all([load(), extras()]);
    return {
      Component: () => (
        <>
          {extra}
          <Page />
        </>
      ),
    };
  };

const VEH = 'veh' as const;
const EXP = 'exp' as const;

export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      /* ── Vehicles ── */
      {
        path: paths.home,
        // The legacy home rating prompt (P5): 2 s after load, once per tab per client.
        lazy: pageWith(
          () => import('@/features/vehicles/pages/HomePage'),
          async () => {
            const { RatingPrompt } = await import('@/features/account/components/trips/RatingPrompt');
            return <RatingPrompt />;
          },
        ),
        handle: shell({
          universe: VEH,
          hero: true,
          center: 'switch-until-deep',
          top: { kind: 'root', switch: true },
          tab: 'explore',
        }),
      },
      {
        path: `${paths.results}/:categorySlug?`,
        lazy: page(() => import('@/features/vehicles/pages/ResultsPage')),
        handle: shell({
          universe: VEH,
          center: 'pill',
          top: { kind: 'inner', pill: true },
          tab: 'explore',
          parent: paths.home,
        }),
      },
      {
        path: '/listings/listing-details/:id',
        lazy: page(() => import('@/features/vehicles/pages/VehiclePage')),
        handle: shell({
          universe: VEH,
          center: 'pill',
          top: { kind: 'inner' },
          bottomNav: false,
          parent: paths.results,
        }),
      },

      /* ── Booking ── */
      {
        path: '/pages/booking-detail/:carId',
        lazy: page(() => import('@/features/booking/pages/CheckoutPage')),
        handle: shell({
          universe: VEH,
          center: 'secure',
          top: { kind: 'inner', title: 'title.securePayment' },
          bottomNav: false,
          footer: false,
        }),
      },
      {
        path: '/pages/booking-success',
        lazy: page(() => import('@/features/booking/pages/ConfirmationPage')),
        handle: shell({
          universe: VEH,
          center: 'secure',
          top: { kind: 'inner', title: 'title.requestSent' },
          parent: paths.home,
        }),
      },

      /* ── Account ── */
      {
        path: paths.trips,
        lazy: page(() => import('@/features/account/pages/TripsPage')),
        handle: shell({ universe: VEH, center: 'pill', top: { kind: 'root', title: 'title.trips' }, tab: 'trips' }),
      },
      {
        path: paths.favourites,
        lazy: page(() => import('@/features/account/pages/FavouritesPage')),
        handle: shell({
          universe: VEH,
          center: 'pill',
          top: { kind: 'root', title: 'title.favourites' },
          tab: 'favourites',
        }),
      },
      {
        path: paths.profile,
        lazy: page(() => import('@/features/account/pages/ProfilePage')),
        handle: shell({ universe: VEH, center: 'pill', top: { kind: 'root', title: 'title.profile' }, tab: 'profile' }),
      },
      {
        path: paths.messages,
        // Private page: noindex here, as the chat page itself is built elsewhere (drop this if it renders <Seo>).
        lazy: pageWith(
          () => import('@/features/account/pages/MessagesPage'),
          async () => <PrivateSeo title="title.messages" path={paths.messages} />,
        ),
        handle: shell({
          universe: VEH,
          center: 'pill',
          top: { kind: 'inner', title: 'title.messages' },
          tab: 'profile',
          footer: false,
          parent: paths.profile,
        }),
      },
      {
        path: paths.reviews,
        lazy: page(() => import('@/features/account/pages/ReviewsPage')),
        handle: shell({
          universe: VEH,
          center: 'pill',
          top: { kind: 'inner', title: 'title.reviews' },
          tab: 'profile',
          parent: paths.profile,
        }),
      },

      /* ── Auth ── */
      {
        path: paths.login,
        loader: redirectToAlias,
        lazy: page(() => import('@/features/auth/pages/LoginPage')),
        handle: shell({
          universe: VEH,
          center: 'none',
          top: { kind: 'inner', title: 'title.login' },
          bottomNav: false,
          footer: false,
        }),
      },
      {
        path: paths.register,
        loader: redirectToAlias,
        lazy: page(() => import('@/features/auth/pages/RegisterPage')),
        handle: shell({
          universe: VEH,
          center: 'none',
          top: { kind: 'inner', title: 'title.register' },
          bottomNav: false,
          footer: false,
        }),
      },
      {
        path: paths.forgotPassword,
        lazy: page(() => import('@/features/auth/pages/ForgotPasswordPage')),
        handle: shell({
          universe: VEH,
          center: 'none',
          top: { kind: 'inner', title: 'title.password' },
          bottomNav: false,
          footer: false,
          parent: paths.login,
        }),
      },
      {
        path: paths.resetPassword,
        lazy: page(() => import('@/features/auth/pages/ResetPasswordPage')),
        handle: shell({
          universe: VEH,
          center: 'none',
          top: { kind: 'inner', title: 'title.password' },
          bottomNav: false,
          footer: false,
          parent: paths.login,
        }),
      },

      /* ── Partner ── */
      {
        path: paths.partner,
        lazy: page(() => import('@/features/partner/pages/PartnerPage')),
        handle: shell({ universe: VEH, center: 'pill', top: { kind: 'inner', title: 'title.partner' } }),
      },

      /* ── Experiences (behind the platform kill switch) ── */
      {
        element: <ActivitiesGuard />,
        children: [
          {
            path: paths.experiences,
            // Old links `/activities?city=&date=&categoryId=` (P15) fill the experience search.
            lazy: pageWith(
              () => import('@/features/experiences/pages/ExperiencesPage'),
              async () => <LegacyExperienceQuery />,
            ),
            handle: shell({
              universe: EXP,
              hero: true,
              center: 'switch',
              top: { kind: 'root', switch: true },
              tab: 'explore',
            }),
          },
          {
            // City landing page (new URL, Decision 110).
            path: '/activites/:citySlug',
            lazy: page(() => import('@/features/landing/pages/ExperienceCityPage')),
            handle: shell({ universe: EXP, center: 'switch', top: { kind: 'inner' }, parent: paths.experiences }),
          },
          {
            path: '/activities/details/:id',
            lazy: page(() => import('@/features/experiences/pages/ExperienceDetailPage')),
            handle: shell({
              universe: EXP,
              center: 'switch',
              top: { kind: 'inner' },
              bottomNav: false,
              parent: paths.experiences,
            }),
          },
          {
            path: '/activities/:citySlug/:activitySlug/:id',
            lazy: page(() => import('@/features/experiences/pages/ExperienceDetailPage')),
            handle: shell({
              universe: EXP,
              center: 'switch',
              top: { kind: 'inner' },
              bottomNav: false,
              parent: paths.experiences,
            }),
          },
          {
            path: '/activities/checkout/:id',
            lazy: page(() => import('@/features/experiences/pages/ExperienceCheckoutPage')),
            handle: shell({
              universe: EXP,
              center: 'secure',
              top: { kind: 'inner', title: 'title.securePayment' },
              bottomNav: false,
              footer: false,
            }),
          },
          {
            path: '/activities/booking-success/:id?',
            lazy: page(() => import('@/features/experiences/pages/ExperienceBookedPage')),
            handle: shell({
              universe: EXP,
              center: 'secure',
              top: { kind: 'inner', title: 'title.experienceBooked' },
              parent: paths.experiences,
            }),
          },
          {
            path: '/activities/:categorySlug',
            lazy: page(() => import('@/features/experiences/pages/ExperiencesPage')),
            handle: shell({
              universe: EXP,
              hero: true,
              center: 'switch',
              top: { kind: 'root', switch: true },
              tab: 'explore',
            }),
          },
        ],
      },

      /* ── Content ── */
      {
        path: paths.about,
        lazy: page(() => import('@/features/content/pages/AboutPage')),
        handle: shell({ universe: VEH, center: 'pill', top: { kind: 'inner', title: 'title.about' } }),
      },
      {
        path: paths.faq,
        lazy: page(() => import('@/features/content/pages/FaqPage')),
        handle: shell({ universe: VEH, center: 'pill', top: { kind: 'inner', title: 'title.faq' } }),
      },
      {
        path: paths.terms,
        lazy: page(() => import('@/features/content/pages/TermsPage')),
        handle: shell({ universe: VEH, center: 'pill', top: { kind: 'inner', title: 'title.terms' } }),
      },
      {
        path: paths.privacy,
        lazy: page(() => import('@/features/content/pages/PrivacyPage')),
        handle: shell({ universe: VEH, center: 'pill', top: { kind: 'inner', title: 'title.privacy' } }),
      },
      {
        path: paths.contact,
        lazy: page(() => import('@/features/content/pages/ContactPage')),
        handle: shell({ universe: VEH, center: 'pill', top: { kind: 'inner', title: 'title.contact' } }),
      },
      {
        path: paths.blog,
        lazy: page(() => import('@/features/content/pages/BlogPage')),
        handle: shell({ universe: VEH, center: 'pill', top: { kind: 'inner', title: 'title.blog' } }),
      },
      {
        path: '/blog/blog-details/:blogId',
        lazy: page(() => import('@/features/content/pages/BlogPostPage')),
        handle: shell({
          universe: VEH,
          center: 'pill',
          top: { kind: 'inner', title: 'title.article' },
          parent: paths.blog,
        }),
      },
      /* ── City landing pages (new URLs, Decision 110) ── */
      {
        path: '/location-voiture/:citySlug',
        lazy: page(() => import('@/features/landing/pages/CarCityPage')),
        handle: shell({ universe: VEH, center: 'pill', top: { kind: 'inner' }, parent: paths.home }),
      },
      /* ── Their English URLs, shown in English (Decision 112) ── */
      {
        element: <EnglishPages />,
        children: [
          {
            path: '/en/car-rental/:citySlug',
            lazy: page(() =>
              import('@/features/landing/pages/CarCityPage').then((m) => ({ default: m.EnglishCarCityPage })),
            ),
            handle: shell({ universe: VEH, center: 'pill', top: { kind: 'inner' }, parent: paths.home }),
          },
          {
            element: <ActivitiesGuard />,
            children: [
              {
                path: '/en/experiences/:citySlug',
                lazy: page(() =>
                  import('@/features/landing/pages/ExperienceCityPage').then((m) => ({
                    default: m.EnglishExperienceCityPage,
                  })),
                ),
                handle: shell({ universe: EXP, center: 'switch', top: { kind: 'inner' }, parent: paths.experiences }),
              },
            ],
          },
        ],
      },
      {
        path: '/pages/agency/:agencyName',
        lazy: page(() => import('@/features/agency/pages/AgencyPage')),
        handle: shell({ universe: VEH, center: 'pill', top: { kind: 'inner', title: 'title.agency' } }),
      },
      /* ── Old URLs (P15, Decision 26) ── */
      { path: '/index', loader: redirectTo(paths.home) },
      { path: '/user/reservations', loader: redirectTo(paths.trips) },
      {
        path: '*',
        lazy: page(() => import('@/features/content/pages/NotFoundPage')),
        handle: shell({ universe: VEH, center: 'pill', top: { kind: 'inner', title: 'title.notFound' } }),
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
